// Filas de Por comisión, Por tienda y Por producto: el agregado del periodo con los datos de la
// regla, la tienda o el producto, y en 0 lo que no tuvo actividad, como el mock.
import type { RetailCommission } from '@fiado/type-kit';
import { CommissionCountByEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionCountByEnum';
import { CommissionRuleStatusEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionRuleStatusEnum';
import type { ProductCategoryEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ProductCategoryEnum';
import type { ProductTierEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ProductTierEnum';
import type { CatalogProduct } from '@/api/services/retail-catalog/retail-catalog.client';
import type { Store } from '@/api/services/retail-org/retail-org.client';
import { sellerPaidByRule } from './payoutRows';
import { matrixGroupOf, type MatrixGroup } from './ruleMatrix';
import { orEmpty } from './orEmpty';

type AggRow = RetailCommission.CommissionAggregateRow;
type Rule = RetailCommission.CommissionRule;
type Breakdown = RetailCommission.CommissionSellerBreakdown;
type RuleStatus = `${RetailCommission.CommissionRuleStatusEnum}`;
export type Category = `${ProductCategoryEnum}`;
export type Tier = `${ProductTierEnum}`;

const NO_KEYS: ReadonlySet<string> = new Set();

// ── Orden y pills ──

export interface PaidSort<K extends string> {
    key: K;
    direction: 'asc' | 'desc';
}

export function sortRows<T, K extends string>(
    rows: readonly T[],
    sort: PaidSort<K>,
    values: Record<K, (row: T) => string | number>,
): T[] {
    const value = values[sort.key];
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const x = value(a);
        const y = value(b);
        return (
            sign *
            (typeof x === 'number' && typeof y === 'number'
                ? x - y
                : String(x).localeCompare(String(y)))
        );
    });
}

/** Las columnas de texto empiezan ascendente; las cifras, descendente. Repetir la columna invierte. */
export function nextPaidSort<K extends string>(
    current: PaidSort<K>,
    key: K,
    textKeys: readonly K[],
): PaidSort<K> {
    if (current.key === key)
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    return { key, direction: textKeys.includes(key) ? 'asc' : 'desc' };
}

export type ActivityPill = 'all' | 'with' | 'without';

export function activityCounts<T>(
    rows: readonly T[],
    active: (row: T) => boolean,
): Record<ActivityPill, number> {
    const withActivity = rows.filter(active).length;
    return { all: rows.length, with: withActivity, without: rows.length - withActivity };
}

export function applyActivity<T>(
    rows: readonly T[],
    pill: ActivityPill,
    active: (row: T) => boolean,
): T[] {
    if (pill === 'all') return [...rows];
    return rows.filter((row) => active(row) === (pill === 'with'));
}

function matches(query: string, ...texts: ReadonlyArray<string | null>): boolean {
    const q = query.trim().toLowerCase();
    return q === '' || texts.some((text) => (text ?? '').toLowerCase().includes(q));
}

// ── Vendedores ──

/** Con la semana en curso mezclada, el conteo es "al menos": no se sabe quién se repite. */
export interface SellersCount {
    sellersCount: number;
    sellersAtLeast: boolean;
}

export function sellersText(row: SellersCount): string {
    return row.sellersAtLeast ? `${String(row.sellersCount)}+` : String(row.sellersCount);
}

export function sellersChip(row: SellersCount): string {
    const one = row.sellersCount === 1 && !row.sellersAtLeast;
    return `${sellersText(row)} ${one ? 'vendedor' : 'vendedores'}`;
}

export function sellersAtLeastTitle(row: SellersCount): string | undefined {
    return row.sellersAtLeast
        ? `Al menos ${String(row.sellersCount)}: los cortes cerrados y la semana en curso no dicen qué vendedores se repiten.`
        : undefined;
}

// ── Por comisión ──

export interface RulePaidRow extends SellersCount {
    ruleId: string;
    name: string;
    kind: MatrixGroup | null;
    status: RuleStatus | null;
    /** Piezas, o ventas si la regla cuenta por venta. */
    unitsCount: number;
    countsSales: boolean;
    paidCents: number;
}

export type RulePill = 'all' | 'active' | 'inactive' | 'expired';
export type RuleSortKey = 'name' | 'sellers' | 'units' | 'paid';

export const RULE_SORT: Record<RuleSortKey, (row: RulePaidRow) => string | number> = {
    name: (row) => row.name,
    sellers: (row) => row.sellersCount,
    units: (row) => row.unitsCount,
    paid: (row) => row.paidCents,
};

export function buildRuleRows(
    rows: readonly AggRow[],
    rules: readonly Rule[],
    sellersAtLeast: ReadonlySet<string> = NO_KEYS,
): RulePaidRow[] {
    const byId = new Map(rules.map((rule) => [rule.ruleId, rule]));
    const out: RulePaidRow[] = rows.map((row) => {
        const rule = byId.get(row.key);
        return {
            ruleId: row.key,
            name: rule?.name ?? row.name ?? row.key,
            kind: rule ? matrixGroupOf(rule.levers) : null,
            status: rule?.status ?? null,
            sellersCount: row.sellersCount,
            sellersAtLeast: sellersAtLeast.has(row.key),
            unitsCount: row.unitsCount,
            countsSales: rule?.countBy === CommissionCountByEnum.SALE,
            paidCents: row.paidCents,
        };
    });
    const paid = new Set(rows.map((row) => row.key));
    for (const rule of rules) {
        if (paid.has(rule.ruleId) || rule.status === CommissionRuleStatusEnum.DRAFT) continue;
        out.push({
            ruleId: rule.ruleId,
            name: rule.name,
            kind: matrixGroupOf(rule.levers),
            status: rule.status,
            sellersCount: 0,
            sellersAtLeast: false,
            unitsCount: 0,
            countsSales: rule.countBy === CommissionCountByEnum.SALE,
            paidCents: 0,
        });
    }
    return out;
}

export function filterRuleRows(
    rows: readonly RulePaidRow[],
    filters: { search: string; kind: MatrixGroup | 'all' },
): RulePaidRow[] {
    return rows.filter(
        (row) =>
            matches(filters.search, row.name, row.ruleId) &&
            (filters.kind === 'all' || row.kind === filters.kind),
    );
}

/**
 * Activas: vigentes que pagaron en el periodo. Sin activaciones: no pagaron nada, salvo las que
 * solo multiplican, que siempre suman $0 porque su efecto va en las reglas que multiplican.
 * Vencidas: solo las vencidas, sin las pausadas.
 */
const RULE_PILL: Record<RulePill, (row: RulePaidRow) => boolean> = {
    all: () => true,
    active: (row) => row.paidCents > 0 && row.status === 'ACTIVE',
    inactive: (row) => row.paidCents === 0 && row.kind !== 'Multiplicador',
    expired: (row) => row.status === 'EXPIRED',
};

export function rulePillCounts(rows: readonly RulePaidRow[]): Record<RulePill, number> {
    return {
        all: rows.length,
        active: rows.filter(RULE_PILL.active).length,
        inactive: rows.filter(RULE_PILL.inactive).length,
        expired: rows.filter(RULE_PILL.expired).length,
    };
}

export function applyRulePill(rows: readonly RulePaidRow[], pill: RulePill): RulePaidRow[] {
    return rows.filter(RULE_PILL[pill]);
}

// ── Por tienda ──

export interface StorePaidRow extends SellersCount {
    storeId: string;
    name: string;
    code: string;
    zone: string | null;
    salesCount: number;
    placementCents: number;
    paidCents: number;
}

export type StoreSortKey = 'name' | 'sellers' | 'sales' | 'placement' | 'paid';

export const STORE_SORT: Record<StoreSortKey, (row: StorePaidRow) => string | number> = {
    name: (row) => row.name,
    sellers: (row) => row.sellersCount,
    sales: (row) => row.salesCount,
    placement: (row) => row.placementCents,
    paid: (row) => row.paidCents,
};

export function buildStoreRows(
    rows: readonly AggRow[],
    stores: readonly Store[],
    zoneOf: (storeId: string) => string | null,
    sellersAtLeast: ReadonlySet<string> = NO_KEYS,
): StorePaidRow[] {
    const byId = new Map(stores.map((store) => [store.storeId, store]));
    const out: StorePaidRow[] = rows.map((row) => {
        const store = byId.get(row.key);
        return {
            storeId: row.key,
            name: store?.name ?? row.key,
            code: store?.code ?? '',
            zone: zoneOf(row.key),
            sellersCount: row.sellersCount,
            sellersAtLeast: sellersAtLeast.has(row.key),
            salesCount: row.salesCount,
            placementCents: row.placementCents,
            paidCents: row.paidCents,
        };
    });
    const sold = new Set(rows.map((row) => row.key));
    for (const store of stores) {
        if (sold.has(store.storeId)) continue;
        out.push({
            storeId: store.storeId,
            name: store.name,
            code: store.code,
            zone: zoneOf(store.storeId),
            sellersCount: 0,
            sellersAtLeast: false,
            salesCount: 0,
            placementCents: 0,
            paidCents: 0,
        });
    }
    return out;
}

export function filterStoreRows(rows: readonly StorePaidRow[], search: string): StorePaidRow[] {
    return rows.filter((row) => matches(search, row.name, row.code, row.zone));
}

// ── Por producto ──

export interface ProductPaidRow extends SellersCount {
    sku: string;
    name: string;
    priceCents: number | null;
    category: Category | null;
    tier: Tier | null;
    unitsCount: number;
    paidCents: number;
}

export type ProductSortKey = 'sku' | 'name' | 'sellers' | 'units' | 'paid';

export const PRODUCT_SORT: Record<ProductSortKey, (row: ProductPaidRow) => string | number> = {
    sku: (row) => row.sku,
    name: (row) => row.name,
    sellers: (row) => row.sellersCount,
    units: (row) => row.unitsCount,
    paid: (row) => row.paidCents,
};

function productName(product: CatalogProduct | undefined, sku: string): string {
    const name = product ? `${product.brand ?? ''} ${product.model ?? ''}`.trim() : '';
    return name === '' ? sku : name;
}

export function buildProductRows(
    rows: readonly AggRow[],
    products: readonly CatalogProduct[],
    sellersAtLeast: ReadonlySet<string> = NO_KEYS,
): ProductPaidRow[] {
    const bySku = new Map(products.map((product) => [product.sku, product]));
    const rowOf = (
        sku: string,
        values: Pick<
            ProductPaidRow,
            'sellersCount' | 'sellersAtLeast' | 'unitsCount' | 'paidCents'
        >,
    ): ProductPaidRow => {
        const product = bySku.get(sku);
        return {
            sku,
            name: productName(product, sku),
            priceCents: product ? product.basePriceCents : null,
            category: product?.category ?? null,
            tier: product?.tier ?? null,
            ...values,
        };
    };
    const out = rows.map((row) =>
        rowOf(row.key, {
            sellersCount: row.sellersCount,
            sellersAtLeast: sellersAtLeast.has(row.key),
            unitsCount: row.unitsCount,
            paidCents: row.paidCents,
        }),
    );
    const sold = new Set(rows.map((row) => row.key));
    for (const product of products) {
        if (!sold.has(product.sku))
            out.push(
                rowOf(product.sku, {
                    sellersCount: 0,
                    sellersAtLeast: false,
                    unitsCount: 0,
                    paidCents: 0,
                }),
            );
    }
    return out;
}

export function filterProductRows(
    rows: readonly ProductPaidRow[],
    filters: { search: string; tier: Tier | ''; category: Category | '' },
): ProductPaidRow[] {
    return rows.filter(
        (row) =>
            matches(filters.search, row.sku, row.name) &&
            (filters.tier === '' || row.tier === filters.tier) &&
            (filters.category === '' || row.category === filters.category),
    );
}

// ── Quién aportó en el corte en curso ──

export interface Contributor {
    sellerId: string;
    unitsCount: number;
    paidCents: number;
}

export function contributorsOf(
    sellers: readonly Breakdown[],
    by: 'RULE' | 'STORE' | 'SKU',
    key: string,
): Contributor[] {
    const out: Contributor[] = [];
    for (const b of sellers) {
        const part =
            by === 'RULE'
                ? sellerPaidByRule(b).find((r) => r.ruleId === key)
                : by === 'STORE'
                  ? orEmpty(b.byStore).find((s) => s.storeId === key)
                  : orEmpty(b.bySku).find((s) => s.sku === key);
        if (part)
            out.push({
                sellerId: b.sellerId,
                unitsCount: part.unitsCount,
                paidCents: part.paidCents,
            });
    }
    return out.sort((x, y) => y.paidCents - x.paidCents);
}

/** Quién aportó sale siempre del corte en curso; la nota dice cuándo eso no es la fila completa. */
export function contributorsNote(source: {
    source: 'live' | 'range';
    liveOutsidePeriod: boolean;
}): string | null {
    if (source.liveOutsidePeriod)
        return 'La semana en curso no entra en el periodo elegido: estos vendedores no suman en la fila.';
    return source.source === 'range'
        ? 'Solo la semana en curso: los cortes cerrados no guardan quién aportó.'
        : null;
}
